import React from 'react';
import { Box, Grid, Link, Paper, Stack, Typography } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { LegalList, LegalNotice, LegalP, LegalPage, LegalSection } from '../components/Legal/LegalPage';

const ContactCard: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode }> = ({ icon, label, value }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%', borderRadius: 2, borderColor: 'rgba(7,24,45,0.1)' }}>
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box sx={{ width: 38, height: 38, borderRadius: 1.25, bgcolor: '#fff4c7', color: '#9a6a00', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
        {icon}
      </Box>
      <Box minWidth={0}>
        <Typography variant="caption" color="text.secondary" fontWeight={800}>{label}</Typography>
        <Typography variant="body2" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>{value}</Typography>
      </Box>
    </Stack>
  </Paper>
);

const GrievanceOfficer: React.FC = () => {
  const updated = 'May 14, 2026';

  return (
    <LegalPage
      title="Contact / Grievance Officer"
      updated={updated}
      intro="Use this page for privacy, data deletion, listing removal, billing, account, or platform grievance requests."
    >
      <LegalNotice>
        RideSaathi is a driver-focused ride lead platform that aggregates and organizes ride requirements from authorized sources and verified contributors. We review complaints about incorrect, expired, unauthorized, or misleading listings.
      </LegalNotice>

      <LegalSection title="1. Grievance contact">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <ContactCard
              icon={<SupportAgentIcon />}
              label="Grievance Officer"
              value="RideSaathi Grievance Officer, Chahal Travel"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ContactCard
              icon={<EmailIcon />}
              label="Email"
              value={<Link href="mailto:support@ridesaathi.in" underline="hover">support@ridesaathi.in</Link>}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ContactCard
              icon={<LocationOnIcon />}
              label="Address"
              value="Mohali, Chandigarh, Punjab, India"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ContactCard
              icon={<ScheduleIcon />}
              label="Support hours"
              value="Business days, India time"
            />
          </Grid>
        </Grid>
      </LegalSection>

      <LegalSection title="2. What to include">
        <LegalList
          items={[
            'Your name, phone number, and email address.',
            'The type of request: privacy, correction, deletion, opt-out, listing removal, billing, account, safety, or other grievance.',
            'Relevant ride route, date, contact number, listing screenshot, payment reference, or account email.',
            'A clear explanation of what should be corrected, removed, reviewed, or resolved.',
          ]}
        />
      </LegalSection>

      <LegalSection title="3. How we review complaints">
        <LegalP>
          We may acknowledge, verify, investigate, request additional information, mask contact details, remove or expire a listing, correct information, restrict accounts, or reject requests that cannot be verified.
        </LegalP>
        <LegalP>
          Data deletion and opt-out requests can also be submitted through the Data Deletion / Opt-out page so they are stored with a review status.
        </LegalP>
      </LegalSection>

      <LegalSection title="4. Platform and third-party disclaimer">
        <LegalP>
          RideSaathi is only a platform for organizing rides and does not guarantee ride booking, payment, safety, driver or customer verification, or ride completion.
        </LegalP>
        <LegalP>
          RideSaathi does not claim partnership, sponsorship, endorsement, or affiliation with WhatsApp, Meta, Google, Apple, or any third-party platform unless officially stated in writing by RideSaathi and the relevant third party.
        </LegalP>
      </LegalSection>
    </LegalPage>
  );
};

export default GrievanceOfficer;
